import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Award,
  BookOpen,
  Scale,
  Users,
  CheckCircle2,
  FileCheck,
  Sparkles,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';

export function OmKvalitetssikringPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-kf-gray-50 to-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-kf-blue-900 via-kf-blue-800 to-kf-blue-900 text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, rgba(242, 162, 45, 0.4) 0, transparent 50%), radial-gradient(circle at 80% 80%, rgba(242, 162, 45, 0.3) 0, transparent 50%)',
            }}
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid md:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-kf-orange-500/20 text-kf-orange-200 px-4 py-2 rounded-full text-sm font-medium mb-6 ring-1 ring-kf-orange-400/30">
                <ShieldCheck className="w-4 h-4" />
                Kvalitetssikret kurs
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance">
                Verifisert.<br />
                <span className="text-kf-orange-400">Det betyr noe.</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-kf-blue-100 max-w-2xl leading-relaxed">
                Når et kurs bærer dette stempelet, er innholdet gjennomgått og godkjent
                etter voksenopplæringsloven § 10 — med dokumenterte læringsutbytter,
                faglig forankring og tydelig vurderingsform.
              </p>
            </div>
            <div className="flex-shrink-0 flex justify-center md:justify-end">
              <img
                src="https://test.studieplan.kompetanseattest.no/branding/kompetanseforbundet-verified-badge-round.png"
                alt="Kvalitetssikret kurs — Verifisert — Voksenopplæringsloven § 10"
                className="w-56 h-56 sm:w-64 sm:h-64 drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Hva stempelet betyr */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-kf-gray-900 mb-4 text-balance">
            Hva garanterer stempelet?
          </h2>
          <p className="text-lg text-kf-gray-600">
            Fire konkrete krav er oppfylt før et kurs blir verifisert — ikke bare én gang,
            men ved hver versjon av studieplanen.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-kf-gray-200 p-6 hover:border-kf-blue-300 hover:shadow-md transition-all">
            <div className="w-12 h-12 bg-kf-blue-100 text-kf-blue-700 rounded-lg flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-kf-gray-900 mb-2">
              Dokumenterte læringsutbytter
            </h3>
            <p className="text-kf-gray-600 text-sm leading-relaxed">
              Studieplanen beskriver hva kandidaten skal <em>kunne</em>, <em>gjøre</em>{' '}
              og <em>mestre</em> etter kurset — strukturert etter Blooms taksonomi og
              forankret i Nasjonalt kvalifikasjonsrammeverk (NKR / EQF).
            </p>
          </div>

          <div className="bg-white rounded-xl border border-kf-gray-200 p-6 hover:border-kf-blue-300 hover:shadow-md transition-all">
            <div className="w-12 h-12 bg-kf-blue-100 text-kf-blue-700 rounded-lg flex items-center justify-center mb-4">
              <FileCheck className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-kf-gray-900 mb-2">
              Vurdering som faktisk tester læringen
            </h3>
            <p className="text-kf-gray-600 text-sm leading-relaxed">
              Hvert læringsutbytte er koblet til en vurderingsform — skriftlig prøve,
              praktisk oppgave, prosjekt, portefølje, muntlig eller observasjon — med
              klare kriterier for bestått.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-kf-gray-200 p-6 hover:border-kf-blue-300 hover:shadow-md transition-all">
            <div className="w-12 h-12 bg-kf-blue-100 text-kf-blue-700 rounded-lg flex items-center justify-center mb-4">
              <Scale className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-kf-gray-900 mb-2">
              Godkjent av et studieforbund
            </h3>
            <p className="text-kf-gray-600 text-sm leading-relaxed">
              Studieforbundet som eier kurset har gjennomgått studieplanen etter forskrift
              om studieforbund godkjent av Kunnskapsdepartementet. Ansvar og eierskap er
              dokumentert.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-kf-gray-200 p-6 hover:border-kf-blue-300 hover:shadow-md transition-all">
            <div className="w-12 h-12 bg-kf-blue-100 text-kf-blue-700 rounded-lg flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-kf-gray-900 mb-2">
              System for validering
            </h3>
            <p className="text-kf-gray-600 text-sm leading-relaxed">
              Studieplanen gjennomgår en strukturert kvalitetsvurdering mot konstruktiv
              linje, målbarhet, ESCO-ferdighetskobling og EU-standarder. Fagpersoner i
              studieforbundet kontrollerer og konkluderer før attester kan utstedes —
              hele vurderingen er logget og sporbar.
            </p>
          </div>
        </div>
      </section>

      {/* Slik kvalitetssikres det */}
      <section className="bg-kf-blue-50 border-y border-kf-blue-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-kf-gray-900 mb-4 text-balance">
              Hvordan et kurs blir verifisert
            </h2>
            <p className="text-lg text-kf-gray-600">
              Prosessen er den samme hver gang — fra et nytt kurs legges inn til en
              kompetanseattest utstedes etter bestått vurdering.
            </p>
          </div>

          <ol className="space-y-4 max-w-3xl mx-auto">
            {[
              {
                title: 'Studieplanen utarbeides',
                body: 'Kursleverandøren beskriver mål, læringsutbytter, innhold, varighet (studiepoeng/ECTS) og vurderingsform. Alt forankres i NKR-nivå og ESCO-ferdigheter.',
              },
              {
                title: 'Systematisk kvalitetsgjennomgang',
                body: 'Studieplanen sjekkes mot 9 kvalitetsdimensjoner — konstruktiv linje, målbarhet, NKR-nivå, ESCO-forankring og EU-standarder. Automatiserte kontrollregler flagger uklare læringsutbytter og manipulasjonsforsøk. Fagpersoner i studieforbundet gjør den endelige vurderingen.',
              },
              {
                title: 'Godkjenning av studieforbundet',
                body: 'Et studieforbund godkjent av Kunnskapsdepartementet gir formell godkjenning av studieplanen. Dette er organisasjonen som står ansvarlig for den faglige kvaliteten.',
              },
              {
                title: 'Vurderingen gjennomføres',
                body: 'Kandidaten tar prøven, leverer prosjektet eller gjennomfører den praktiske vurderingen. Sensor med dokumentert fagkompetanse vurderer besvarelsen.',
              },
              {
                title: 'Kompetanseattest utstedes',
                body: 'Ved bestått får kandidaten en digitalt signert attest (eSeal fra Buypass) som kan verifiseres av arbeidsgivere, utdanningsinstitusjoner og andre tredjeparter.',
              },
            ].map((step, idx) => (
              <li
                key={idx}
                className="flex gap-4 bg-white rounded-xl border border-kf-blue-100 p-5 shadow-sm"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-kf-blue-900 text-white rounded-full flex items-center justify-center font-bold">
                  {idx + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-kf-gray-900 mb-1">{step.title}</h3>
                  <p className="text-kf-gray-600 text-sm leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Juridisk forankring */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-kf-gray-100 text-kf-gray-700 px-3 py-1 rounded-full text-xs font-medium mb-4">
              <Scale className="w-3.5 h-3.5" />
              Juridisk grunnlag
            </div>
            <h2 className="text-3xl font-bold text-kf-gray-900 mb-4 text-balance">
              Voksenopplæringsloven § 10
            </h2>
            <p className="text-kf-gray-600 leading-relaxed mb-4">
              Studieforbund godkjent av Kunnskapsdepartementet må følge{' '}
              <strong>§ 10 i voksenopplæringsloven</strong> og tilhørende forskrift. Kravet
              innebærer blant annet dokumenterte studieplaner, kvalitetssikring av
              vurdering og åpenhet om hvordan læringsutbytter oppnås.
            </p>
            <p className="text-kf-gray-600 leading-relaxed">
              Verifiseringsstempelet er en synliggjøring av at kurset oppfyller disse
              kravene — og at Kompetanseforbundet har gjennomgått systemene som sørger for
              etterlevelse.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-kf-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-kf-blue-700" />
              <h3 className="text-lg font-semibold text-kf-gray-900">
                Hvem står bak stempelet?
              </h3>
            </div>
            <p className="text-kf-gray-600 text-sm leading-relaxed mb-4">
              <strong>Kompetanseforbundet</strong> er fellesorganet for
              studieforbundene godkjent av Kunnskapsdepartementet. Studieforbundene våre
              representerer medlemsorganisasjoner som tilbyr kurs innen bygg, helse,
              ledelse, HMS, pedagogikk og andre fagfelt.
            </p>
            <p className="text-kf-gray-600 text-sm leading-relaxed">
              Verifiserings-tjenesten driftes av Kompetanseforbundet med sertifisering via
              Buypass eSeal (eIDAS) og underleverandører registrert i vårt offentlige
              underdatabehandler-register.
            </p>
          </div>
        </div>
      </section>

      {/* Verifiser-en-attest + Kompetanseheving CTA */}
      <section className="bg-kf-gray-50 border-t border-kf-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl border border-kf-gray-200 p-7">
              <div className="w-12 h-12 bg-kf-blue-100 text-kf-blue-700 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-kf-gray-900 mb-2">
                Har du mottatt en kompetanseattest?
              </h3>
              <p className="text-kf-gray-600 text-sm leading-relaxed mb-5">
                Du kan verifisere ektheten av attesten via QR-kode eller
                verifikasjonskoden som står på dokumentet.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-kf-blue-700 hover:text-kf-blue-900 font-medium text-sm"
              >
                Logg inn på kompetanseattest.no
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white rounded-xl border border-kf-gray-200 p-7">
              <div className="w-12 h-12 bg-kf-orange-100 text-kf-orange-700 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-kf-gray-900 mb-2">
                Vil du finne et verifisert kurs?
              </h3>
              <p className="text-kf-gray-600 text-sm leading-relaxed mb-5">
                På kompetanseheving.no finner du oversikten over kvalitetssikrede kurs
                fra Kompetanseforbundets medlemsorganisasjoner.
              </p>
              <a
                href="https://www.kompetanseheving.no"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-kf-orange-700 hover:text-kf-orange-900 font-medium text-sm"
              >
                Besøk kompetanseheving.no
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer-note */}
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-kf-gray-500">
        <p>
          Dette er en offentlig informasjonsside fra Kompetanseforbundet.
          {' '}
          <a
            href="https://www.kompetanseforbundet.no"
            target="_blank"
            rel="noopener noreferrer"
            className="text-kf-blue-700 hover:text-kf-blue-900 underline underline-offset-2"
          >
            kompetanseforbundet.no
          </a>
        </p>
      </footer>
    </div>
  );
}
